import { useApolloClient } from "@apollo/client";
import { LoadingSpinner, ModalOpenButton } from "@msys/ui";
import LinkIcon from "@mui/icons-material/Link";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Button,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikHelpers } from "formik";
import React from "react";
import * as Yup from "yup";
import { useUserData } from "../../auth/useUserData.js";
import { ColorPickerField } from "../../commons/form-fields/ColorPickerField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { GetFileContentButton } from "../../commons/modals/GetFileContentButton.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { GetUrlContentModal } from "../../commons/modals/GetUrlContentModal.js";
import {
  useOrganisationWizardStylesQuery,
  useSetOrganisationWizardCustomStylesMutation,
} from "./OrganisationWizardStyling.generated.js";

interface FormValues {
  fontFaceDefinitions: string;
  fontFamily: string;
  fontSizeGroupTitle: string;
  fontWeightGroupTitle: string;
  fontSizeQuestionPrompt: string;
  fontWeightQuestionPrompt: string;
  fontSizeOptionLabel: string;
  fontWeightOptionLabel: string;
  fontSizeButtonText: string;
  fontWeightButtonText: string;
  primaryColor: string;
  secondaryColor: string;
  selectedColor: string;
}

interface Props {
  submenuItems: PageTopbarItem[];
}

export const OrganisationWizardStyling = ({ submenuItems }: Props) => {
  const viewer = useUserData().currentUser!;
  const { t } = useTranslate([
    "OrganisationBranding",
    "OrganisationPageTopbar",
    "Global",
  ]);

  const client = useApolloClient();
  const query = useOrganisationWizardStylesQuery({ client });
  const styles = query.data?.organisationWizardStyles;

  const [setWizardStyles] = useSetOrganisationWizardCustomStylesMutation({
    client,
  });
  const handleSubmit = React.useCallback(
    async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
      await setWizardStyles({
        variables: {
          input: { organisationId: viewer.organisation.id, styles: values },
        },
      });
      formikHelpers.resetForm({ values });
    },
    [setWizardStyles, viewer.organisation.id]
  );

  return (
    <Page
      title={t("Wizard", {
        ns: "OrganisationBranding",
      })}
      subtitle={t("Branding", { ns: "OrganisationPageTopbar" })}
      submenuItems={submenuItems}
    >
      <PageContainer>
        {query.loading ? (
          <LoadingSpinner />
        ) : (
          <Formik<FormValues>
            initialValues={{
              fontFaceDefinitions: styles?.fontFaceDefinitions || "",
              fontFamily: styles?.fontFamily || "",
              fontSizeGroupTitle: styles?.fontSizeGroupTitle || "",
              fontWeightGroupTitle: styles?.fontWeightGroupTitle || "",
              fontSizeQuestionPrompt: styles?.fontSizeQuestionPrompt || "",
              fontWeightQuestionPrompt: styles?.fontWeightQuestionPrompt || "",
              fontSizeOptionLabel: styles?.fontSizeOptionLabel || "",
              fontWeightOptionLabel: styles?.fontWeightOptionLabel || "",
              fontSizeButtonText: styles?.fontSizeButtonText || "",
              fontWeightButtonText: styles?.fontWeightButtonText || "",
              primaryColor: styles?.primaryColor || "",
              secondaryColor: styles?.secondaryColor || "",
              selectedColor: styles?.selectedColor || "",
            }}
            validationSchema={Yup.object({})}
            onSubmit={handleSubmit}
          >
            {formikProps => (
              <Form>
                <Stack
                  spacing={4}
                  sx={{
                    maxWidth: 600,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="h2">
                      {t("Fonts", { ns: "OrganisationBranding" })}
                    </Typography>
                    <Stack direction={"row"} spacing={1}>
                      <TextField
                        name="fontFaceDefinitions"
                        label={t("Font-face definitions", {
                          ns: "OrganisationBranding",
                        })}
                        multiline
                        rows={6}
                        helperText={t(
                          "This should contain only @font-face definitions. You can get these definitions from eg. Google Fonts.",
                          {
                            ns: "OrganisationBranding",
                          }
                        )}
                      />
                      <Stack>
                        <ModalOpenButton
                          Modal={GetUrlContentModal}
                          modalProps={{
                            handleComplete: (content, handleClose) => {
                              formikProps.setFieldValue(
                                "fontFaceDefinitions",
                                content
                              );
                              handleClose();
                            },
                          }}
                        >
                          <IconButton color="primary">
                            <Tooltip
                              title={t("Load from URL", {
                                ns: "OrganisationBranding",
                              })}
                            >
                              <LinkIcon />
                            </Tooltip>
                          </IconButton>
                        </ModalOpenButton>
                        <GetFileContentButton
                          onComplete={content => {
                            formikProps.setFieldValue(
                              "fontFaceDefinitions",
                              content
                            );
                          }}
                        >
                          <IconButton color="primary">
                            <Tooltip
                              title={t("Load from file", {
                                ns: "OrganisationBranding",
                              })}
                            >
                              <UploadFileIcon />
                            </Tooltip>
                          </IconButton>
                        </GetFileContentButton>
                      </Stack>
                    </Stack>
                    <TextField
                      name="fontFamily"
                      label={t("Font-family", { ns: "OrganisationBranding" })}
                      helperText={t(
                        "When using non-standard font-family, make sure to add font-face definitions above to load these fonts",
                        {
                          ns: "OrganisationBranding",
                        }
                      )}
                    />
                    <Grid container spacing={1} columns={2}>
                      <Grid size={{ xs: 2 }}>
                        <Typography variant="h3">
                          {t("Group title", { ns: "OrganisationBranding" })}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <FontSizeInputField
                          name="fontSizeGroupTitle"
                          label={t("Font size", {
                            ns: "OrganisationBranding",
                          })}
                        />
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <FontWeightInputField
                          name="fontWeightGroupTitle"
                          label={t("Font weight", {
                            ns: "OrganisationBranding",
                          })}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1} columns={2}>
                      <Grid size={{ xs: 2 }}>
                        <Typography variant="h3">
                          {t("Question prompt", { ns: "OrganisationBranding" })}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <FontSizeInputField
                          name="fontSizeQuestionPrompt"
                          label={t("Font size", {
                            ns: "OrganisationBranding",
                          })}
                        />
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <FontWeightInputField
                          name="fontWeightQuestionPrompt"
                          label={t("Font weight", {
                            ns: "OrganisationBranding",
                          })}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1} columns={2}>
                      <Grid size={{ xs: 2 }}>
                        <Typography variant="h3">
                          {t("Option label", { ns: "OrganisationBranding" })}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <FontSizeInputField
                          name="fontSizeOptionLabel"
                          label={t("Font size", {
                            ns: "OrganisationBranding",
                          })}
                        />
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <FontWeightInputField
                          name="fontWeightOptionLabel"
                          label={t("Font weight", {
                            ns: "OrganisationBranding",
                          })}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1} columns={2}>
                      <Grid size={{ xs: 2 }}>
                        <Typography variant="h3">
                          {t("Button text", { ns: "OrganisationBranding" })}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <FontSizeInputField
                          name="fontSizeButtonText"
                          label={t("Font size", {
                            ns: "OrganisationBranding",
                          })}
                        />
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <FontWeightInputField
                          name="fontWeightButtonText"
                          label={t("Font weight", {
                            ns: "OrganisationBranding",
                          })}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                  <Stack spacing={1}>
                    <Grid container spacing={1} columns={2}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="h2">
                          {t("Colors", { ns: "OrganisationBranding" })}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <ColorPickerField
                          label={t("Primary color", {
                            ns: "OrganisationBranding",
                          })}
                          name="primaryColor"
                          helperText={t(
                            // @ts-ignore FIXME
                            "This color is used for most foreground elements, eg. texts, buttons",
                            { ns: "OrganisationBranding" }
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <ColorPickerField
                          label={t("Secondary color", {
                            ns: "OrganisationBranding",
                          })}
                          name="secondaryColor"
                          helperText={t(
                            // @ts-ignore FIXME
                            "This color is used for alternative foreground elements, eg. question prompt texts",
                            { ns: "OrganisationBranding" }
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 2, md: 1 }}>
                        <ColorPickerField
                          label={t("Selected color", {
                            ns: "OrganisationBranding",
                          })}
                          name="selectedColor"
                          helperText={t(
                            // @ts-ignore FIXME
                            "This color is used for highlighting selected options",
                            { ns: "OrganisationBranding" }
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                  <Alert severity="info">
                    {t("Changes will take effect in about 10 minutes.", {
                      ns: "OrganisationBranding",
                    })}
                  </Alert>
                  <Stack
                    sx={{
                      alignItems: "flex-end",
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={formikProps.isSubmitting || !formikProps.dirty}
                    >
                      {t("Save", { ns: "Global" })}
                    </Button>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        )}
      </PageContainer>
    </Page>
  );
};

const FontSizeInputField = ({
  name,
  label,
}: {
  name: string;
  label: string;
}) => {
  const { t } = useTranslate(["OrganisationBranding"]);

  return (
    <TextField
      name={name}
      label={label}
      helperText={t("Eg. {example}", {
        ns: "OrganisationBranding",
        example: "14px, 1.4rem, 2em",
      })}
    />
  );
};

const FontWeightInputField = ({
  name,
  label,
}: {
  name: string;
  label: string;
}) => {
  const { t } = useTranslate(["OrganisationBranding"]);

  return (
    <TextField
      name={name}
      label={label}
      helperText={t("Eg. {example}", {
        ns: "OrganisationBranding",
        example: "normal, bold, 500",
      })}
    />
  );
};
